import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { exchangeGoogleCode } from '../api/googleOAuthClient';
import { useAuthStore } from '../state/authStore';
import { useSessionStore } from '../../style-transfer/state/sessionStore';

type CallbackStatus = 'loading' | 'success' | 'error';

function Spinner({ className = 'h-12 w-12' }: { className?: string }) {
    return (
        <svg
            className={`${className} animate-spin text-ai-primary`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
}

function CheckIcon({ className = 'h-12 w-12' }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
    );
}

function XIcon({ className = 'h-12 w-12' }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

export function GoogleCallbackPage() {
    const { t } = useTranslation();
    const [status, setStatus] = useState<CallbackStatus>('loading');
    const [error, setError] = useState<string | null>(null);
    const callbackRequest = useRef<ReturnType<typeof exchangeGoogleCode> | null>(null);
    const { mutateAsync } = useMutation({ mutationFn: exchangeGoogleCode, retry: false });

    useEffect(() => {
        let active = true;
        let redirectTimer: number | undefined;
        const exchangeCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');
            const state = params.get('state');
            const errorParam = params.get('error');
            window.history.replaceState(null, '', window.location.pathname);
            if (errorParam) {
                throw new Error(t('account.oauth.userDenied'));
            }
            if (!code) {
                throw new Error(t('account.oauth.missingCode'));
            }
            const savedState = sessionStorage.getItem('oauth_state');
            const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
            if (!state || state !== savedState || !codeVerifier) {
                throw new Error(t('account.oauth.stateMismatch'));
            }
            return mutateAsync({ code, state, codeVerifier });
        };
        const handleOAuthCallback = async () => {
            try {
                callbackRequest.current ??= exchangeCallback();
                const authResult = await callbackRequest.current;
                if (!active) {
                    return;
                }
                useAuthStore.getState().setAuthenticated(
                    authResult.email, authResult.token, authResult.userName
                );
                useSessionStore.getState().setToken(authResult.token);
                setStatus('success');
                redirectTimer = window.setTimeout(() => { window.location.href = '/'; }, 1500);
            } catch (error) {
                if (active) {
                    setStatus('error');
                    setError(error instanceof TypeError ? t('account.oauth.networkError')
                        : error instanceof Error ? error.message : t('account.errors.generic'));
                }
            } finally {
                sessionStorage.removeItem('oauth_state');
                sessionStorage.removeItem('oauth_code_verifier');
            }
        };
        void handleOAuthCallback();
        return () => {
            active = false;
            window.clearTimeout(redirectTimer);
        };
    }, [mutateAsync, t]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-ai-surface-base via-ai-surface-elevated to-ai-surface-base px-4">
            <div className="w-full max-w-md rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/90 p-8 text-center shadow-ai-card">
                {status === 'loading' && (
                    <>
                        <Spinner className="mx-auto mb-4" />
                        <h2 className="mb-2 text-xl font-semibold text-ai-text-primary">
                            {t('account.oauth.processing')}
                        </h2>
                        <p className="text-sm text-ai-text-secondary">{t('account.oauth.pleaseWait')}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <CheckIcon className="mx-auto mb-4 text-ai-positive" />
                        <h2 className="mb-2 text-xl font-semibold text-ai-text-primary">
                            {t('account.oauth.success')}
                        </h2>
                        <p className="text-sm text-ai-text-secondary">{t('account.oauth.redirecting')}</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <XIcon className="mx-auto mb-4 text-ai-danger" />
                        <h2 className="mb-2 text-xl font-semibold text-ai-text-primary">
                            {t('account.oauth.failed')}
                        </h2>
                        <p className="mb-6 text-sm text-ai-danger">{error}</p>
                        <button
                            onClick={() => (window.location.href = '/')}
                            className="rounded-full bg-ai-primary px-6 py-2.5 text-sm font-semibold text-white shadow-ai-glow transition-colors hover:bg-ai-secondary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                        >
                            {t('account.oauth.backToHome')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
